import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router";
import type { Checkpoint, Message, Thread } from "@langchain/langgraph-sdk";
import { type UseStreamOptions, useStream } from "@langchain/langgraph-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getUrl } from "src/basepath";
import { DEFAULT_TAB_SEARCH_PARAM } from "src/components/general/Tabs/Tabs.constants";
import { useQueryAppInfo } from "src/queries/info/info.query";
import { useQueryTags } from "src/queries/tags/tags.query";
import { useCommonQueryParams } from "src/queries/useCommonQueryParams";
import { useQueryUsersPublic } from "src/queries/users/users.query";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { userFullName } from "src/utils/user";
import {
  ACTIVE_THREAD_STORAGE_KEY,
  AI_ASSISTANTS_QUERY_KEY,
  AI_THREADS_QUERY_KEY,
  COMPARE_RUNS_CONTEXT_LABELS,
  DEFAULT_ASSISTANT_ID,
  DEFAULT_SESSION_TITLE,
  MESSAGE_CONTEXT_KWARG,
  MESSAGE_CONTEXT_URL_KWARG,
  THREADS_PAGE_SIZE,
} from "./AiToolboxSection.constants";
import {
  formatAiContextForPrompt,
  useAiContextItems,
} from "./AiToolboxSection.context";
import { groupDisplayContent } from "./AiToolboxSection.display";
import { useLlmFetch } from "./AiToolboxSection.fetch";
import {
  attachSubagentDisplay,
  isForbiddenError,
  messageContentToString,
  messagesToDisplayContent,
  type SubagentSource,
  subagentSourceToDisplayInfo,
} from "./AiToolboxSection.stream";
import type { ChatSession, SimpleChatRequest } from "./AiToolboxSection.types";
import {
  buildContextLines,
  formatTimeWindowForContext,
  generateId,
  getCompareRunsContextValue,
  getContextLabels,
  getPageType,
  type SubagentDisplayEntries,
  subagentDisplayCache,
} from "./AiToolboxSection.utils";

const ASSISTANT_API_URL = getUrl("/assistant-api");

export function useFilterDisplayNames() {
  const { data: tags } = useQueryTags();
  const { data: users } = useQueryUsersPublic();

  const tagNamesById = useMemo(
    () => new Map(tags?.map((t) => [t.tag_id, t.name])),
    [tags]
  );
  const userNamesById = useMemo(
    () => new Map(users?.map((u) => [u.user_id, userFullName(u) || u.email])),
    [users]
  );

  return { tagNamesById, userNamesById };
}

/** State shape for the ai-agents-manager graph */
type AgentState = { messages: Message[] };

type SubmitCheckpoint = Omit<Checkpoint, "thread_id"> | Checkpoint;

/** A transcript segment: one human message, or the ai/tool block following it */
interface TranscriptRow {
  type: "human" | "ai";
  /** First SDK message of the segment — carries the row id and, for retry/edit,
   * the handle passed to `getMessagesMetadata` for checkpoint forking. */
  sdkMessage: Message;
  /** ai/tool messages of an AI block (absent for human rows) */
  block?: Message[];
}

/**
 * Routes subagent-namespace messages into `stream.subagents` (keyed by the
 * spawning `task` tool_call_id) instead of interleaving them into the main
 * transcript. Supported at runtime by useStream but only declared on its
 * deep-agent typed overload, hence the assertion.
 */
const SUBAGENT_STREAM_OPTIONS = {
  filterSubagentMessages: true,
} as Partial<UseStreamOptions<AgentState>>;

const readStoredThreadId = () => {
  try {
    return localStorage.getItem(ACTIVE_THREAD_STORAGE_KEY);
  } catch {
    return null;
  }
};

const threadToSession = (thread: Thread): ChatSession => ({
  id: thread.thread_id,
  title:
    ((thread.metadata as Record<string, unknown> | undefined)
      ?.thread_name as string) || DEFAULT_SESSION_TITLE,
  createdAt: Date.parse(thread.created_at),
  updatedAt: Date.parse(thread.updated_at),
});

/**
 * Chat hook backed by the ai-agents-manager LangGraph server.
 *
 * Agent-protocol threads are the source of truth: the transcript derives from
 * `stream.messages` (server-hydrated on mount), the history sidebar from
 * `threads.search`, and titles from server-assigned `metadata.thread_name`.
 * The only client-side state is the active-thread pointer (localStorage) and
 * a per-thread subagent transcript cache (IndexedDB, display fidelity only).
 */
export function useChatStream() {
  const queryClient = useQueryClient();

  // ── Active thread pointer ────────────────────────────────────────────────
  const [activeThreadId, setActiveThreadIdState] = useState<string | null>(
    readStoredThreadId
  );
  const setActiveThreadId = useCallback((id: string | null) => {
    setActiveThreadIdState(id);
    try {
      if (id) {
        localStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
      }
    } catch {
      // Storage unavailable — pointer just won't survive a reload
    }
  }, []);
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;

  // Threads created in this session — excluded from the stale-pointer check
  // below, since the thread list may not include them until refetch completes.
  const createdThreadIdsRef = useRef<Set<string>>(new Set());

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);

  // ── Context derivation ───────────────────────────────────────────────────
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isCompareRunsPage = pathname.includes("/compare-runs");
  const isClusterPage = pathname.includes("/computes/");
  const pageType = getPageType(pathname);

  const commonParams = useCommonQueryParams([
    "env_name",
    "app_name",
    "app_pit",
    "task_id",
    "task_name",
    "tf_id",
    "ds_name",
    "timeWindow",
    "appTags",
    "appOwners",
  ]);

  const { tagNamesById, userNamesById } = useFilterDisplayNames();

  const tabValue = searchParams.get(DEFAULT_TAB_SEARCH_PARAM);
  const contextParams = useMemo(
    () => ({
      page: pageType,
      ...commonParams,
      ...(tabValue && { tab: tabValue }),
    }),
    [pageType, commonParams, tabValue]
  );

  const dynamicContextItems = useAiContextItems();
  const dynamicContext = useMemo(
    () => formatAiContextForPrompt(dynamicContextItems),
    [dynamicContextItems]
  );

  const context = useMemo(() => {
    const baseContext = isCompareRunsPage
      ? buildContextLines(COMPARE_RUNS_CONTEXT_LABELS, (key) =>
          getCompareRunsContextValue(key, pageType, searchParams)
        )
      : buildContextLines(getContextLabels(isClusterPage), (key) => {
          if (key === "timeWindow") {
            return formatTimeWindowForContext(contextParams[key]);
          }
          if (key === "appTags") {
            const ids = contextParams[key];
            if (!Array.isArray(ids) || ids.length === 0) return undefined;
            return ids.map((id) => tagNamesById.get(id) ?? id).join(", ");
          }
          if (key === "appOwners") {
            const ids = contextParams[key];
            if (!Array.isArray(ids) || ids.length === 0) return undefined;
            return ids.map((id) => userNamesById.get(id) ?? id).join(", ");
          }
          return contextParams[key];
        });

    return [baseContext, dynamicContext].filter(Boolean).join("\n\n") || undefined;
  }, [
    isCompareRunsPage,
    isClusterPage,
    pageType,
    searchParams,
    contextParams,
    dynamicContext,
    tagNamesById,
    userNamesById,
  ]);

  // ── Auth via custom fetch (injects fresh token on every SDK request) ─────
  // callerOptions.fetch is the correct injection point for this SDK version
  // (@langchain/langgraph-sdk does not expose a top-level `fetch` option).
  // llmFetch identity is stable (useRef inside useLlmFetch), so this memo
  // produces the same object reference across renders.
  const llmFetch = useLlmFetch();
  const callerOptions = useMemo(() => ({ fetch: llmFetch }), [llmFetch]);

  // ── Subagent transcript cache (per-thread, display fidelity only) ────────
  const [cachedSubagents, setCachedSubagents] = useState<SubagentDisplayEntries>({});
  useEffect(() => {
    let cancelled = false;
    setCachedSubagents({});
    if (!activeThreadId) return;
    void subagentDisplayCache.get(activeThreadId).then((entries) => {
      if (!cancelled && entries) setCachedSubagents(entries);
    });
    return () => {
      cancelled = true;
    };
  }, [activeThreadId]);

  // ── Assistant (aegra.json graph) ─────────────────────────────────────────
  // Debug mode can pick another graph; the choice applies from the next run.
  const isDebugMode = useGlobalLocalContext((state) => state.isDebugMode);
  const assistantIdOverride = useGlobalLocalContext(
    (state) => state.aiAssistantIdOverride
  );
  const setAssistantIdOverride = useGlobalLocalContext(
    (state) => state.setAiAssistantIdOverride
  );
  const assistantId = (isDebugMode && assistantIdOverride) || DEFAULT_ASSISTANT_ID;

  // ── LangGraph useStream ──────────────────────────────────────────────────
  // Stable callback — prevent useStream from re-subscribing on every render.
  const onThreadId = useCallback(
    (id: string) => {
      createdThreadIdsRef.current.add(id);
      setActiveThreadId(id);
      void queryClient.invalidateQueries({ queryKey: AI_THREADS_QUERY_KEY });
    },
    [setActiveThreadId, queryClient]
  );

  // Refs read by the onFinish callback (stable identity, fresh values).
  const subagentsRef = useRef<ReadonlyMap<string, SubagentSource>>(new Map());

  // A submit whose run id has not arrived yet, and a Stop clicked in that
  // window. Aborting the request then could leave a run nobody can cancel
  // (runs outlive a disconnect), so the stop waits for the run to exist.
  const awaitingRunRef = useRef(false);
  const stopOnRunCreatedRef = useRef(false);
  const streamRef = useRef<{ stop: () => Promise<void> } | null>(null);

  const onCreated = useCallback(() => {
    awaitingRunRef.current = false;
    if (stopOnRunCreatedRef.current) {
      stopOnRunCreatedRef.current = false;
      void streamRef.current?.stop();
    }
  }, []);

  const onFinish = useCallback(() => {
    // Refresh sidebar metadata (thread_name, updated_at) after every run.
    void queryClient.invalidateQueries({ queryKey: AI_THREADS_QUERY_KEY });

    // Snapshot live subagent transcripts — subgraph state is not retrievable
    // from the server once the run completes.
    const threadId = activeThreadIdRef.current;
    const subagents = subagentsRef.current;
    if (!threadId || subagents.size === 0) return;
    const entries: SubagentDisplayEntries = {};
    for (const [toolCallId, source] of subagents) {
      entries[toolCallId] = subagentSourceToDisplayInfo(source, toolCallId);
    }
    setCachedSubagents((prev) => ({ ...prev, ...entries }));
    void subagentDisplayCache.merge(threadId, entries);
  }, [queryClient]);

  // The stream object is stable across renders (backed by useSyncExternalStore),
  // so it is safe to list in dep arrays — callbacks referencing it never recreate.
  const stream = useStream<AgentState>({
    apiUrl: ASSISTANT_API_URL,
    assistantId,
    callerOptions,
    threadId: activeThreadId,
    onThreadId,
    // Thread history enables branch metadata (getMessagesMetadata) so retry
    // and edit fork from checkpoints instead of appending duplicates, and
    // powers the branch navigation arrows. The window is deliberately larger
    // than the SDK default (10): forks older than the window lose their
    // navigation and fall back to append-style retry.
    fetchStateHistory: { limit: 25 },
    messagesKey: "messages",
    // Disable batching so each token event triggers an immediate React render,
    // giving true character-by-character streaming rather than batched flushes.
    throttle: false,
    // Keeps the running run's id in sessionStorage: `stream.stop()` then cancels
    // the run server-side (runs submit with onDisconnect "continue", so aborting
    // the fetch alone leaves the graph running), and a reload or a switch back
    // to the thread rejoins a run that is still going.
    reconnectOnMount: true,
    onCreated,
    onFinish,
    ...SUBAGENT_STREAM_OPTIONS,
  });
  streamRef.current = stream;
  const sdkMessages = stream.messages as Message[];
  const isLoading = stream.isLoading;
  const isThreadLoading = stream.isThreadLoading;
  const streamError = stream.error instanceof Error ? stream.error : null;

  // Live subagent streams keyed by the spawning `task` tool_call_id.
  // Present at runtime on the useStream return but, like the option above,
  // only declared on the deep-agent typed overload.
  // The getter builds a fresh Map on each access, so read it once per render.
  const subagentSources =
    (stream as Partial<{ subagents: ReadonlyMap<string, SubagentSource> }>)
      .subagents ?? new Map<string, SubagentSource>();
  subagentsRef.current = subagentSources;

  // ── Sessions (ai-agents-manager threads) ────────────────────────────────────
  const threadsQuery = useQuery({
    queryKey: AI_THREADS_QUERY_KEY,
    // Wire React Query's signal into the SDK so unmount-cancellation aborts
    // the fetch cleanly instead of leaving an unhandled AbortError rejection.
    queryFn: ({ signal }) =>
      stream.client.threads.search({
        limit: THREADS_PAGE_SIZE,
        sortBy: "updated_at",
        sortOrder: "desc",
        signal,
      }),
    staleTime: 30_000,
  });

  const forbiddenError = [streamError, threadsQuery.error].find(isForbiddenError);
  const error = forbiddenError ?? streamError;
  const isAssistantDisabled = Boolean(forbiddenError);

  useEffect(() => {
    if (!isAssistantDisabled) return;
    void queryClient.invalidateQueries({ queryKey: [useQueryAppInfo.url] });
  }, [isAssistantDisabled, queryClient]);

  // Debug mode lists the graphs Aegra serves: it registers one assistant per graph.
  const assistantsQuery = useQuery({
    queryKey: AI_ASSISTANTS_QUERY_KEY,
    queryFn: ({ signal }) => stream.client.assistants.search({ limit: 50, signal }),
    enabled: isDebugMode,
    staleTime: 5 * 60_000,
  });
  const assistantIds = useMemo(
    () =>
      [
        ...new Set([
          assistantId,
          ...(assistantsQuery.data ?? []).map((assistant) => assistant.graph_id),
        ]),
      ].sort(),
    [assistantId, assistantsQuery.data]
  );
  // Choosing the default clears the override, so a later default change applies.
  const setAssistantId = useCallback(
    (id: string) => setAssistantIdOverride(id === DEFAULT_ASSISTANT_ID ? null : id),
    [setAssistantIdOverride]
  );

  const sessions = useMemo(
    () =>
      (threadsQuery.data ?? [])
        .map(threadToSession)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [threadsQuery.data]
  );

  // Self-heal a stale pointer (thread deleted elsewhere, wiped dev DB): only a
  // 404 on the thread itself resets to a new chat. Absence from `threads.search`
  // proves nothing, since that list is capped at THREADS_PAGE_SIZE.
  useEffect(() => {
    if (!activeThreadId || createdThreadIdsRef.current.has(activeThreadId)) {
      return;
    }
    const controller = new AbortController();
    stream.client.threads
      .get(activeThreadId, { signal: controller.signal })
      .catch((err: unknown) => {
        if ((err as { status?: number } | null)?.status === 404) {
          setActiveThreadId(null);
        }
      });
    return () => controller.abort();
  }, [activeThreadId, stream, setActiveThreadId]);

  // ── Transcript rows derived from SDK messages ────────────────────────────
  const rows = useMemo((): TranscriptRow[] => {
    const out: TranscriptRow[] = [];
    let block: Message[] = [];
    const flushBlock = () => {
      if (block.length > 0) {
        out.push({ type: "ai", sdkMessage: block[0], block });
        block = [];
      }
    };
    for (const msg of sdkMessages) {
      if (msg.type === "human") {
        flushBlock();
        out.push({ type: "human", sdkMessage: msg });
      } else if (
        msg.type === "ai" ||
        (msg.type as string) === "AIMessageChunk" ||
        msg.type === "tool"
      ) {
        block.push(msg);
      }
    }
    flushBlock();
    return out;
  }, [sdkMessages]);

  // Branch metadata comes from the fetched thread history; watch it so the
  // navigation appears as soon as the history (re)fetch lands, even when the
  // message list itself didn't change identity.
  let streamHistory: unknown[] | undefined;
  try {
    streamHistory = stream.history;
  } catch {
    streamHistory = undefined;
  }

  /**
   * Sibling-branch metadata for a row. Both fork kinds mark the whole branch
   * segment: a retry fork's segment starts at the regenerated response, an
   * edit fork's at the edited human message — and every state in the segment
   * (including the response that follows an edit) carries the same
   * branch/branchOptions, so switching always swaps a consistent
   * human+response pairing.
   *
   * Guard against history-window truncation: a message older than the fetched
   * window inherits the window's oldest state as its `firstSeenState` and
   * would spuriously carry that state's fork options — pressing the arrows
   * there would switch an unrelated fork further down the thread. Only trust
   * a fork whose fork-point (parent) state is itself inside the window.
   */
  const resolveRowBranchMeta = useCallback(
    (index: number) => {
      const states = (streamHistory ?? []) as {
        checkpoint?: { checkpoint_id?: string };
      }[];
      // The SDK puts fork metadata on the first message of a branch segment
      // only (a retry fork's regenerated response, an edit fork's human
      // message) and blanks it for the rest of the segment.
      const metaOf = (row: TranscriptRow) => {
        const meta = stream.getMessagesMetadata?.(row.sdkMessage);
        if (meta?.branch == null || (meta.branchOptions?.length ?? 0) <= 1) {
          return undefined;
        }
        // Guard against history-window truncation: a message older than the
        // fetched window resolves its `firstSeenState` to the window's oldest
        // state and would inherit that state's fork options — pressing the
        // arrows there would switch an unrelated fork elsewhere in the
        // thread. Only trust a fork whose fork-point (parent) state is
        // itself inside the window.
        const forkPointId = meta.firstSeenState?.parent_checkpoint?.checkpoint_id;
        if (
          !forkPointId ||
          !states.some((s) => s.checkpoint?.checkpoint_id === forkPointId)
        ) {
          return undefined;
        }
        return meta as { branch: string; branchOptions: string[] };
      };

      const row = rows[index];
      if (!row) return undefined;
      const own = metaOf(row);
      if (own) return own;
      // Edit forks carry their metadata on the human message; surface them on
      // the response below it (switching swaps the whole segment, so the
      // human + response pairing stays consistent).
      if (row.type === "ai") {
        for (let i = index - 1; i >= 0; i--) {
          if (rows[i].type === "human") return metaOf(rows[i]);
        }
      }
      return undefined;
    },
    [rows, stream, streamHistory]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: streamHistory feeds resolveRowBranchMeta via stream internals — branch metadata must refresh when the history (re)fetch lands
  const transcript = useMemo(
    (): SimpleChatRequest<ReactNode[]>[] =>
      rows.map((row, index) => {
        if (row.type === "human") {
          const kwargs = (
            row.sdkMessage as { additional_kwargs?: Record<string, unknown> }
          ).additional_kwargs;
          return {
            type: "human" as const,
            id: row.sdkMessage.id,
            content: [messageContentToString(row.sdkMessage.content)],
            context: (kwargs?.[MESSAGE_CONTEXT_KWARG] as string) || undefined,
            contextUrl: (kwargs?.[MESSAGE_CONTEXT_URL_KWARG] as string) || undefined,
          };
        }
        const items = attachSubagentDisplay(
          messagesToDisplayContent(row.block ?? []),
          subagentSources,
          cachedSubagents
        );
        const branchMeta = resolveRowBranchMeta(index);
        return {
          type: "ai" as const,
          id: row.sdkMessage.id,
          content: groupDisplayContent(items),
          ...(branchMeta && {
            branchInfo: {
              position:
                Math.max(0, branchMeta.branchOptions.indexOf(branchMeta.branch)) + 1,
              total: branchMeta.branchOptions.length,
            },
          }),
        };
      }),
    [rows, subagentSources, cachedSubagents, resolveRowBranchMeta, streamHistory]
  );

  // While streaming, the forming AI block renders as the streaming bubble
  // (typing indicator when the run hasn't produced ai/tool messages yet).
  const isStreamingAiBlock =
    isLoading && rows.length > 0 && rows[rows.length - 1].type === "ai";
  const uiMessages = isStreamingAiBlock ? transcript.slice(0, -1) : transcript;
  const streamingContentNodes = isStreamingAiBlock
    ? (transcript[transcript.length - 1].content as ReactNode[])
    : null;

  // ── submit / send ────────────────────────────────────────────────────────
  // Last submission (input + fork point), so the error-banner retry can
  // resubmit exactly what failed — failed messages never reach the thread.
  const lastSubmissionRef = useRef<{
    values: AgentState | undefined;
    checkpoint?: SubmitCheckpoint;
  } | null>(null);

  const submitToThread = useCallback(
    async (values: AgentState | undefined, checkpoint?: SubmitCheckpoint) => {
      lastSubmissionRef.current = { values, checkpoint };
      // Pin the run to the current branch head when not explicitly forking:
      // a submit without a checkpoint resets useStream's visible branch to the
      // default path, which on a forked thread can flip the transcript to a
      // sibling branch after the post-run history refetch.
      const branchHead = (() => {
        try {
          return stream.history?.at(-1)?.checkpoint ?? undefined;
        } catch {
          return undefined;
        }
      })();
      const submitCheckpoint = checkpoint ?? branchHead;
      awaitingRunRef.current = true;
      stopOnRunCreatedRef.current = false;
      try {
        await stream.submit(values, {
          streamMode: ["values", "messages-tuple", "custom"],
          streamSubgraphs: true,
          streamResumable: true,
          onDisconnect: "continue",
          multitaskStrategy: "rollback",
          ...(submitCheckpoint && { checkpoint: submitCheckpoint }),
          // Show the human message immediately instead of waiting for the first
          // `values` event from the server.
          ...(values?.messages?.length && {
            optimisticValues: (prev: Partial<AgentState>) => ({
              ...prev,
              messages: [...(prev.messages ?? []), ...values.messages],
            }),
          }),
          context: {
            page_context: context ?? "",
            base_url: window.location.origin,
          },
        });
      } finally {
        awaitingRunRef.current = false;
        stopOnRunCreatedRef.current = false;
      }
    },
    [context, stream]
  );

  const buildHumanMessage = useCallback(
    (content: string): Message => ({
      id: generateId(),
      type: "human",
      content,
      additional_kwargs: {
        ...(context && { [MESSAGE_CONTEXT_KWARG]: context }),
        [MESSAGE_CONTEXT_URL_KWARG]:
          window.location.pathname + window.location.search,
      },
    }),
    [context]
  );

  const send = useCallback(
    async (message: string) => {
      if (!message.trim()) return;
      await submitToThread({ messages: [buildHumanMessage(message)] });
    },
    [submitToThread, buildHumanMessage]
  );

  // Aborts the stream and interrupts the run (POST /threads/{id}/runs/{id}/cancel).
  const stop = useCallback(() => {
    if (awaitingRunRef.current) {
      stopOnRunCreatedRef.current = true;
      return;
    }
    void stream.stop();
  }, [stream]);

  const abort = useCallback(() => {
    void stream.stop();
  }, [stream]);

  // ── Session management ───────────────────────────────────────────────────
  // Changing the thread only detaches (useStream clears its stream on a
  // threadId change); the run keeps going and is rejoined on switching back.
  const createNewSession = useCallback(() => {
    setActiveThreadId(null);
  }, [setActiveThreadId]);

  const switchSession = useCallback(
    (sessionId: string) => {
      setActiveThreadId(sessionId);
    },
    [setActiveThreadId]
  );

  const deleteSession = useCallback(
    (sessionId: string) => {
      void (async () => {
        try {
          await stream.client.threads.delete(sessionId);
        } catch {
          // Already gone server-side — still clean up locally below
        }
        void subagentDisplayCache.remove(sessionId);
        if (activeThreadIdRef.current === sessionId) {
          const next = sessions.find((s) => s.id !== sessionId);
          setActiveThreadId(next?.id ?? null);
        }
        void queryClient.invalidateQueries({ queryKey: AI_THREADS_QUERY_KEY });
      })();
    },
    [stream, sessions, setActiveThreadId, queryClient]
  );

  const renameSession = useCallback(
    (sessionId: string, newTitle: string) => {
      const trimmedTitle = newTitle.trim();
      if (!trimmedTitle) return;
      // Optimistic sidebar update; the invalidate below reconciles.
      queryClient.setQueryData<Thread[]>(AI_THREADS_QUERY_KEY, (prev) =>
        prev?.map((t) =>
          t.thread_id === sessionId
            ? { ...t, metadata: { ...t.metadata, thread_name: trimmedTitle } }
            : t
        )
      );
      void stream.client.threads
        .update(sessionId, { metadata: { thread_name: trimmedTitle } })
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: AI_THREADS_QUERY_KEY });
        });
    },
    [stream, queryClient]
  );

  // ── Retry / edit (checkpoint forks) ──────────────────────────────────────
  /** Parent checkpoint of the state a row's first message was created in —
   * submitting with it forks the thread there (a branch), replaying instead
   * of appending a duplicate to the stateful thread. */
  const getRowForkCheckpoint = useCallback(
    (row: TranscriptRow): SubmitCheckpoint | undefined => {
      const meta = stream.getMessagesMetadata?.(row.sdkMessage);
      return meta?.firstSeenState?.parent_checkpoint ?? undefined;
    },
    [stream]
  );

  const findPreviousHumanRow = useCallback(
    (startIndex: number) => {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (rows[i].type === "human") return rows[i];
      }
      return undefined;
    },
    [rows]
  );

  const retry = useCallback(
    (messageId: string) => {
      const index = rows.findIndex((r) => r.sdkMessage.id === messageId);
      if (index === -1) return;
      const row = rows[index];

      if (row.type === "ai") {
        // Regenerate: rerun from just before this AI block.
        const checkpoint = getRowForkCheckpoint(row);
        if (checkpoint) {
          void submitToThread(undefined, checkpoint);
          return;
        }
      }

      // Fallback (no branch metadata, e.g. beyond the fetched history window):
      // resend the preceding human message as a new turn.
      const humanRow = row.type === "human" ? row : findPreviousHumanRow(index);
      if (!humanRow) return;
      void send(messageContentToString(humanRow.sdkMessage.content));
    },
    [rows, getRowForkCheckpoint, findPreviousHumanRow, submitToThread, send]
  );

  const retryLast = useCallback(() => {
    // A failed run leaves nothing on the thread — resubmit what was last sent.
    if (error && lastSubmissionRef.current) {
      const { values, checkpoint } = lastSubmissionRef.current;
      void submitToThread(values, checkpoint);
      return;
    }
    const lastAiRow = rows.findLast((r) => r.type === "ai");
    if (lastAiRow?.sdkMessage.id) {
      retry(lastAiRow.sdkMessage.id);
      return;
    }
    const lastHumanRow = rows.findLast((r) => r.type === "human");
    if (lastHumanRow) {
      void send(messageContentToString(lastHumanRow.sdkMessage.content));
    }
  }, [error, rows, retry, send, submitToThread]);

  /** Show the previous/next sibling branch of a retried or edited response. */
  const switchMessageBranch = useCallback(
    (messageId: string, delta: -1 | 1) => {
      if (isLoading) return;
      const index = rows.findIndex((r) => r.sdkMessage.id === messageId);
      if (index === -1) return;
      const meta = resolveRowBranchMeta(index);
      if (!meta) return;
      const target =
        meta.branchOptions[meta.branchOptions.indexOf(meta.branch) + delta];
      if (target != null) stream.setBranch(target);
    },
    [rows, isLoading, resolveRowBranchMeta, stream]
  );

  // ── Message editing ──────────────────────────────────────────────────────
  const startEditMessage = useCallback(
    (messageId: string) => {
      if (isLoading) return;
      const row = rows.find((r) => r.sdkMessage.id === messageId);
      if (row?.type !== "human") return;
      setEditingMessageId(messageId);
    },
    [rows, isLoading]
  );

  const cancelEditMessage = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  const submitEditMessage = useCallback(
    (messageId: string, newContent: string) => {
      setEditingMessageId(null);
      if (!newContent.trim()) return;
      // Fork from just before the edited message so the new content replaces
      // it (and everything after it) on a new branch.
      const row = rows.find(
        (r) => r.type === "human" && r.sdkMessage.id === messageId
      );
      const checkpoint = row ? getRowForkCheckpoint(row) : undefined;
      void submitToThread({ messages: [buildHumanMessage(newContent)] }, checkpoint);
    },
    [rows, getRowForkCheckpoint, submitToThread, buildHumanMessage]
  );

  return {
    messages: uiMessages,
    streamingContent: streamingContentNodes,
    isLoading,
    isThreadLoading,
    error,
    send,
    abort,
    stop,
    retry,
    retryLast,
    switchMessageBranch,
    context,
    editingMessageId,
    startEditMessage,
    cancelEditMessage,
    submitEditMessage,
    sessions,
    activeSessionId: activeThreadId,
    createNewSession,
    switchSession,
    deleteSession,
    renameSession,
    assistantId,
    assistantIds,
    setAssistantId,
  };
}
